import React, { memo, useEffect, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeGrid, GridChildComponentProps, GridOnItemsRenderedProps, areEqual } from 'react-window';
import { isMobile as isMobileDevice } from 'react-device-detect';
import type { CasinoCategory, CasinoGame, CasinoProvider } from '../../../Casino/Type/CasinoTypes';
import { cleanProviderName, getProviderIconUrl } from '../../../Casino/CasinoHelper';
import { GameCard, SkeletonCard } from './CasinoGameCard';
import styles from '../../../assets/css/components/CasinoLobby.module.css';

type ProvidersById = Map<string, CasinoProvider>;
type CategoriesById = Map<string, CasinoCategory>;

type ItemData = {
  games: CasinoGame[];
  providersById: ProvidersById;
  categoriesById: CategoriesById;
  columnCount: number;
  cardWidth: number;
  cardHeight: number;
  gap: number;
  itemCount: number;
  gamesCount: number;
  launchingGameId: string | null;
  onLaunch: (game: CasinoGame, demo: boolean) => void;
};

const SKELETON_COUNT = 12;

const Cell = memo(({ columnIndex, rowIndex, style, data }: GridChildComponentProps<ItemData>) => {
  const itemIndex = rowIndex * data.columnCount + columnIndex;
  if (itemIndex >= data.itemCount) return null;

  const cellStyle: React.CSSProperties = {
    ...style,
    paddingRight: data.gap,
    paddingBottom: data.gap,
    boxSizing: 'border-box',
  };

  const wrapperStyle: React.CSSProperties = {
    width: data.cardWidth,
    height: data.cardHeight,
    flex: '0 0 auto',
  };

  if (itemIndex >= data.gamesCount) {
    return (
      <div style={cellStyle}>
        <div style={wrapperStyle}>
          <SkeletonCard />
        </div>
      </div>
    );
  }

  const game = data.games[itemIndex];
  const provider = game?.providerId ? data.providersById.get(game.providerId) : undefined;
  const providerName = provider?.title ? cleanProviderName(provider.title) : null;
  const providerIconUrl = getProviderIconUrl(provider);

  const gameCategories = (game?.categoryIds || [])
    .map((categoryId) => data.categoriesById.get(categoryId))
    .filter((category): category is CasinoCategory => Boolean(category));

  return (
    <div style={cellStyle}>
      <div style={wrapperStyle}>
        <GameCard
          game={game}
          providerName={providerName}
          providerIconUrl={providerIconUrl}
          gameCategories={gameCategories}
          launchingGameId={data.launchingGameId}
          onLaunch={data.onLaunch}
          taggedGame={false}
        />
      </div>
    </div>
  );
}, areEqual);
Cell.displayName = 'CasinoGamesVirtualCell';

export interface CasinoGamesVirtualListProps {
  games: CasinoGame[];
  total: number;
  providersById: ProvidersById;
  categoriesById: CategoriesById;
  launchingGameId: string | null;
  loadingMore: boolean;
  onLaunch: (game: CasinoGame, demo: boolean) => void;
  onLoadMore: () => void;
  initialScrollOffset?: number;
  onScrollOffsetChange?: (scrollOffset: number) => void;
  scrollToTopTrigger?: number;
}

const CasinoGamesVirtualList: React.FC<CasinoGamesVirtualListProps> = ({
  games,
  total,
  providersById,
  categoriesById,
  launchingGameId,
  loadingMore,
  onLaunch,
  onLoadMore,
  initialScrollOffset,
  onScrollOffsetChange,
  scrollToTopTrigger,
}) => {
  const gridRef = useRef<FixedSizeGrid<ItemData> | null>(null);
  const loadMoreRequestedRef = useRef(false);

  const hasMore = games.length < total;

  // Imperative scroll-to-top support (used by the floating button in the lobby).
  useEffect(() => {
    if (!scrollToTopTrigger) return;
    gridRef.current?.scrollTo({ scrollLeft: 0, scrollTop: 0 });
  }, [scrollToTopTrigger]);

  useEffect(() => {
    if (!loadingMore) {
      loadMoreRequestedRef.current = false;
    }
  }, [loadingMore]);

  return (
    <AutoSizer>
      {({ width, height }) => {
        const gap = isMobileDevice ? 10 : 16;
        const minCardWidth = 170;
        const contentHeight = 172;

        const columnCount = Math.max(1, Math.floor((width + gap) / (minCardWidth + gap)));
        const cardWidth = Math.floor((width - gap * (columnCount - 1)) / columnCount);
        const cardHeight = contentHeight;
        const cellWidth = cardWidth + gap;
        const cellHeight = cardHeight + gap;

        const itemCount = games.length + (loadingMore ? SKELETON_COUNT : 0);
        const rowCount = Math.max(1, Math.ceil(itemCount / columnCount));

        const itemData: ItemData = {
          games,
          providersById,
          categoriesById,
          columnCount,
          cardWidth,
          cardHeight,
          gap,
          itemCount,
          gamesCount: games.length,
          launchingGameId,
          onLaunch,
        };

        const handleItemsRendered = ({ overscanRowStopIndex }: GridOnItemsRenderedProps) => {
          if (!hasMore || loadingMore || loadMoreRequestedRef.current) return;
          if (overscanRowStopIndex >= Math.max(0, rowCount - 3)) {
            loadMoreRequestedRef.current = true;
            onLoadMore();
          }
        };

        return (
          <FixedSizeGrid
            ref={gridRef}
            height={height}
            width={width}
            columnCount={columnCount}
            rowCount={rowCount}
            columnWidth={cellWidth}
            rowHeight={cellHeight}
            itemData={itemData}
            overscanRowCount={3}
            overscanColumnCount={1}
            initialScrollTop={initialScrollOffset}
            className={styles.gamesContainer}
            style={{ overflowX: 'hidden' }}
            onItemsRendered={handleItemsRendered}
            onScroll={({ scrollTop }) => {
              onScrollOffsetChange?.(scrollTop);
            }}
          >
            {Cell}
          </FixedSizeGrid>
        );
      }}
    </AutoSizer>
  );
};

export default CasinoGamesVirtualList;
